import { useState, useEffect } from 'react';
import { Lock, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Shown when a write is refused because the subscription has lapsed.
 *
 * Deliberately not dismissible: unlike a plan-limit warning, this does not
 * clear itself by waiting or by trying something smaller. Every write stays
 * refused until the balance is settled, so a banner the user can close would
 * just make the next failure inexplicable.
 */
export default function SubscriptionBlockedBanner() {
    const [detail, setDetail] = useState(null);

    useEffect(() => {
        const handler = (e) => setDetail(e.detail);
        window.addEventListener('echohire:company-blocked', handler);
        return () => window.removeEventListener('echohire:company-blocked', handler);
    }, []);

    if (!detail) return null;

    const due = Number(detail.amountDue) || 0;

    return (
        <div className="mb-4 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3">
            <div className="flex items-start gap-3">
                <Lock className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-destructive">
                        {detail.subscriptionStatus === 'cancelled'
                            ? 'Subscription cancelled'
                            : 'Subscription ended'}
                        {due > 0 && ` — Rs. ${due.toLocaleString('en-IN')} outstanding`}
                    </p>
                    <p className="mt-1 text-sm text-foreground/80">
                        {detail.message}
                    </p>
                    <p className="mt-1.5 text-xs text-muted-foreground">
                        You can still view everything — candidates, reports and past interviews.
                        Interviews already scheduled will still run for your candidates.
                    </p>
                </div>
                <div className="flex shrink-0 flex-col gap-1.5">
                    <Button
                        size="sm"
                        variant="outline"
                        className="gap-1.5 border-destructive text-xs text-destructive hover:bg-destructive/10"
                        onClick={() => window.location.assign('/admin/plan')}
                    >
                        {due > 0 ? 'View invoice' : 'View plan'} <ExternalLink className="h-3 w-3" />
                    </Button>
                    <Button
                        size="sm"
                        variant="ghost"
                        className="gap-1.5 text-xs"
                        onClick={() => window.location.assign('/admin/support')}
                    >
                        Ask for help
                    </Button>
                </div>
            </div>
        </div>
    );
}
